'use client';

import { API_ROUTES, useApiMutation, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { cn } from '@shared/components/shadcn';
import { getSiteConfig } from '@shared/config';
import { createDebug } from '@shared/debug';
import { buildPlayerTrackerImpressionPayload, PLAYER_TRACKER_IMPRESSION, usePlayerTrackerServiceEvent } from '@shared/tracking';
import { NumberFormats as nf, useCurrencyFormatter } from '@shared/utils';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useRef, useState } from 'react';
import { Link } from '../../Link/Link';
import type { EditableNavigationMenuItemProps, LinkNavigationMenuItemProps } from '../../NavigationMenu/components/NavigationMenuItem/types';
import { NavigationMenu } from '../../NavigationMenu/NavigationMenu';
import { toast } from '../../Toast/Toast';
import { HiddenTrigger } from '../MyAccountSheet/MyAccountSheet';
import { RealityCheckSheet } from './components/RealityCheckSheet';
import { SelfExclusionSheet } from './components/SelfExclusionSheet/SelfExclusionSheet';
import { TimeOutSheet } from './components/TimeOutSheet/TimeOutSheet';
import { useDepositLimitsQuery } from './hooks/useDepositLimitsQuery';
import { SetDepositLimitSheet } from './SetDepositLimit/components/SetDepositLimitSheet';
import { getTrackTypeForPeriod, getTranslationKeyForPeriod } from './SetDepositLimit/utils/utils';
import type { DepositLimit, LimitPeriod, RealityCheckResponse } from './types';
import { getDepositLimitPath, getDepositLimitsPath, getPendingDepositLimitPath, isEncodedPendingLimitRemoval } from './utils/playerLimits';

const debug = createDebug({ isEnabled: false });

const ONE_HOUR_MS = 1000 * 60 * 60;
const ONE_DAY_MS = ONE_HOUR_MS * 24;
const FALLBACK_HOURS_PENDING_LIMIT_VALID_FROM = 24;
const FALLBACK_DAYS_PENDING_LIMIT_VALID_FROM = 7;

function getHoursUntilPendingLimitValidFrom(validFrom: string | undefined, fallbackHours = FALLBACK_HOURS_PENDING_LIMIT_VALID_FROM): number {
  if (!validFrom) {
    return fallbackHours;
  }

  const instant = new Date(validFrom);

  if (Number.isNaN(instant.getTime())) {
    return fallbackHours;
  }

  const diffMs = instant.getTime() - Date.now();

  return Math.max(0, Math.ceil(diffMs / ONE_HOUR_MS));
}

function getDaysUntilPendingLimitValidFrom(validFrom: string | undefined, fallbackDays = FALLBACK_DAYS_PENDING_LIMIT_VALID_FROM): number {
  if (!validFrom) {
    return fallbackDays;
  }

  const instant = new Date(validFrom);

  if (Number.isNaN(instant.getTime())) {
    return fallbackDays;
  }

  const diffMs = instant.getTime() - Date.now();

  return Math.max(0, Math.ceil(diffMs / ONE_DAY_MS));
}

type SaferGamblingProps = {
  namespace: string;
  className?: string;
};

function SaferGambling({ namespace, className }: SaferGamblingProps) {
  const { session } = useSession();
  const t = useTranslations(namespace);
  const queryClient = useQueryClient();
  const siteConfig = getSiteConfig();
  const saferGambling = siteConfig.SAFER_GAMBLING;
  const isAustralianMarket = siteConfig.REGISTRATION.country === 'AU';
  const [showRealityCheckSheet, setShowRealityCheckSheet] = useState(false);
  const [showTimeOutSheet, setShowTimeOutSheet] = useState(false);
  const [showSetDepositLimitSheet, setShowSetDepositLimitSheet] = useState(false);
  const [showSelfExclusionSheet, setShowSelfExclusionSheet] = useState(false);
  const formatter = useCurrencyFormatter();
  const [limitPeriod, setLimitPeriod] = useState<LimitPeriod>('DAY');
  const [updatingPeriod, setUpdatingPeriod] = useState<LimitPeriod | null>(null);
  const ptsTrackEvent = usePlayerTrackerServiceEvent();
  const settingsContainerRef = useRef<HTMLDivElement>(null);

  const playerId = session?.profile?.id;

  const { data: depositLimits, isLoading: isDepositLimitsLoading } = useDepositLimitsQuery(!!playerId);
  const trackDepositLimitSetConfirmation = () => {
    const payload = buildPlayerTrackerImpressionPayload(PLAYER_TRACKER_IMPRESSION.DEPOSIT_LIMIT_SET_CONFIRMATION, session?.profile, settingsContainerRef.current);
    if (!payload) return;

    ptsTrackEvent(payload);
  };

  const { data: realityCheckData, isLoading: isRealityCheckLoading } = useApiQuery<RealityCheckResponse>(API_ROUTES.MY_ACCOUNT.REALITY_CHECK.GET, {
    queryOptions: {
      enabled: saferGambling.showRealityCheck,
    },
    credentials: true,
  });

  const calculateResetTime = (period: LimitPeriod, expirationTime?: string) => {
    if (!expirationTime) return '';
    const now = new Date();
    const expiry = new Date(expirationTime);
    const diffInMilliseconds = expiry.getTime() - now.getTime();
    const diffInHours = Math.ceil(diffInMilliseconds / (1000 * 60 * 60));
    const diffInDays = Math.ceil(diffInMilliseconds / (1000 * 60 * 60 * 24));

    switch (period) {
      case 'DAY':
        return t('deposit_limits.resets_in_hours', { hours: diffInHours });
      case 'WEEK':
      case 'MONTH':
        return t('deposit_limits.resets_in_days', { days: diffInDays });
      default:
        return '';
    }
  };

  const createMutationOptions = () => ({
    mutationOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['api', getDepositLimitsPath()] });
      },
      onError: (error: Error) => {
        debug.forceError('Error updating deposit limit:', error);
      },
    },
    requestOptions: {
      credentials: true,
    },
  });

  const acceptLimit = {
    DAY: useApiMutation<void, void>(getPendingDepositLimitPath('DAY'), 'POST', createMutationOptions()),
    WEEK: useApiMutation<void, void>(getPendingDepositLimitPath('WEEK'), 'POST', createMutationOptions()),
    MONTH: useApiMutation<void, void>(getPendingDepositLimitPath('MONTH'), 'POST', createMutationOptions()),
  };

  const declineLimit = {
    DAY: useApiMutation<void, void>(getPendingDepositLimitPath('DAY'), 'DELETE', createMutationOptions()),
    WEEK: useApiMutation<void, void>(getPendingDepositLimitPath('WEEK'), 'DELETE', createMutationOptions()),
    MONTH: useApiMutation<void, void>(getPendingDepositLimitPath('MONTH'), 'DELETE', createMutationOptions()),
  };

  const removeLimit = {
    DAY: useApiMutation<void, void>(getDepositLimitPath('DAY'), 'DELETE', createMutationOptions()),
    WEEK: useApiMutation<void, void>(getDepositLimitPath('WEEK'), 'DELETE', createMutationOptions()),
    MONTH: useApiMutation<void, void>(getDepositLimitPath('MONTH'), 'DELETE', createMutationOptions()),
  };

  const createDepositLimitItem = (period: LimitPeriod): EditableNavigationMenuItemProps => {
    const limit = depositLimits?.limits.find(limit => limit.period === period);
    const isAcceptancePending = saferGambling.depositLimitsRequireAcceptance && Boolean(limit?.pendingLimit?.confirmationNeeded);
    const pendingLimitForPeriod = isAcceptancePending ? limit?.pendingLimit : undefined;
    const pendingLimitInCoolOff = limit?.pendingLimit && !isAcceptancePending ? limit.pendingLimit : undefined;
    const hasExistingLimit = limit !== undefined && limit.limitValue !== undefined && limit.limitValue > 0;
    const isUpdating = updatingPeriod === period;

    const getPendingCoolOffText = () => {
      if (!pendingLimitInCoolOff) return undefined;

      const isRemoval = isEncodedPendingLimitRemoval(pendingLimitInCoolOff.value);
      const { pendingRaise, pendingRemoval } = saferGambling.coolOffStatusKeys;
      const duration =
        saferGambling.coolOffUnit === 'days'
          ? getDaysUntilPendingLimitValidFrom(pendingLimitInCoolOff.validFrom, saferGambling.coolOffFallback)
          : getHoursUntilPendingLimitValidFrom(pendingLimitInCoolOff.validFrom, saferGambling.coolOffFallback);

      return t(`deposit_limits.${isRemoval ? pendingRemoval : pendingRaise}`, {
        [saferGambling.coolOffUnit]: duration,
        ...(isRemoval ? {} : { amount: formatter(pendingLimitInCoolOff.value).format(nf.WITHOUT_DECIMALS).get() }),
      });
    };

    const getPendingReadyText = () => {
      if (!pendingLimitForPeriod) return undefined;
      if (isEncodedPendingLimitRemoval(pendingLimitForPeriod.value)) {
        return t('deposit_limits.pending_removal');
      }

      return t('deposit_limits.pending_change', {
        amount: formatter(pendingLimitForPeriod.value).format(nf.WITHOUT_DECIMALS).get(),
      });
    };

    const handleAcceptForPeriod = () => {
      if (!pendingLimitForPeriod) {
        return;
      }

      const currentLimitValue = depositLimits?.limits.find(limitForPeriod => limitForPeriod.period === period)?.limitValue ?? 0;
      const periodTranslation = t(`deposit_limits.${getTranslationKeyForPeriod(period)}`).toLowerCase();
      const formattedAcceptedAmount = formatter(pendingLimitForPeriod.value).format(nf.WITHOUT_DECIMALS).get();
      const formattedStableAmountForRemovalCopy = formatter(currentLimitValue).format(nf.WITHOUT_DECIMALS).get();
      const isPendingRemoval = isEncodedPendingLimitRemoval(pendingLimitForPeriod.value);

      acceptLimit[period].mutate(undefined, {
        onSuccess: () => {
          trackDepositLimitSetConfirmation();
          toast(
            isPendingRemoval
              ? t('deposit_limits.removed_toast')
              : t('deposit_limits.accepted_toast', {
                  period: periodTranslation,
                  amount: formattedAcceptedAmount,
                }),
            { type: 'info' }
          );
        },
        onError: () => {
          toast(
            t('deposit_limits.accepted_error_toast', {
              period: periodTranslation,
              amount: isPendingRemoval ? formattedStableAmountForRemovalCopy : formattedAcceptedAmount,
            }),
            { type: 'error' }
          );
        },
      });
    };

    const handleDeclineForPeriod = () => {
      if (!pendingLimitForPeriod) {
        return;
      }

      const currentLimit = depositLimits?.limits.find(limit => limit.period === period);
      const periodTranslation = t(`deposit_limits.${getTranslationKeyForPeriod(period)}`).toLowerCase();
      const formattedAmount = formatter(currentLimit?.limitValue || 0)
        .format(nf.WITHOUT_DECIMALS)
        .get();
      const formattedDeclinedAmount = formatter(pendingLimitForPeriod.value || 0)
        .format(nf.WITHOUT_DECIMALS)
        .get();
      const isPendingRemoval = isEncodedPendingLimitRemoval(pendingLimitForPeriod.value);

      declineLimit[period].mutate(undefined, {
        onSuccess: () => {
          toast(
            t('deposit_limits.declined_toast', {
              period: periodTranslation,
              amount: formattedAmount,
            }),
            { type: 'info' }
          );
        },
        onError: () => {
          toast(
            t('deposit_limits.declined_error_toast', {
              period: periodTranslation,
              amount: isPendingRemoval ? formattedAmount : formattedDeclinedAmount,
            }),
            { type: 'error' }
          );
        },
      });
    };

    const handleRemoveForPeriod = () => {
      removeLimit[period].mutate(undefined, {
        onSuccess: () => {
          trackDepositLimitSetConfirmation();
          toast(t('deposit_limits.removed_toast'), { type: 'info', trackType: 'Deposit Limits', trackMessage: 'Limit Removed' });
        },
        onError: () => {
          toast(t('deposit_limits.removed_error_toast'), { type: 'error', trackType: 'Deposit Limits', trackMessage: 'System Error' });
        },
      });
    };

    const getDataForLimit = (limit: DepositLimit | undefined, hasExistingLimit: boolean) => {
      if (!limit) {
        return t('deposit_limits.not_set');
      }

      // E.g. £0/100 or Not set
      return hasExistingLimit
        ? `${formatter(limit.limitValue! - limit.remaining!)
            .format(nf.WITHOUT_DECIMALS)
            .get()}/${((limit.limitValue! || 0) / 100).toLocaleString('en-GB')}`
        : t('deposit_limits.not_set');
    };

    return {
      label: t(`deposit_limits.${getTranslationKeyForPeriod(period)}`),
      data: getDataForLimit(limit, hasExistingLimit),
      buttonText: hasExistingLimit ? t('edit') : t('add'),
      chevron: false,
      onClick: () => {
        setLimitPeriod(period);
        setShowSetDepositLimitSheet(true);
      },
      variant: 'editable',
      statusText: hasExistingLimit
        ? pendingLimitForPeriod
          ? getPendingReadyText()
          : pendingLimitInCoolOff
            ? getPendingCoolOffText()
            : calculateResetTime(period, limit?.expirationTime)
        : undefined,
      statusIndicatorColor: hasExistingLimit ? saferGambling.depositLimitStatusIndicatorColor : undefined,
      statusAction:
        hasExistingLimit && !pendingLimitForPeriod
          ? {
              label: t('deposit_limits.remove_limit'),
              onClick: handleRemoveForPeriod,
              'data-testid': `remove-limit-${period}-button`,
              'data-track-name': 'Remove Limit',
            }
          : undefined,
      actions:
        hasExistingLimit && pendingLimitForPeriod
          ? [
              {
                label: t('deposit_limits.decline'),
                onClick: handleDeclineForPeriod,
                variant: 'secondary',
                'data-testid': `decline-limit-${period}-button`,
                'data-track-name': 'Decline',
              },
              {
                label: t('deposit_limits.accept'),
                onClick: handleAcceptForPeriod,
                variant: 'primary',
                'data-testid': `accept-limit-${period}-button`,
                'data-track-name': 'Accept',
              },
            ]
          : undefined,
      isLoading: isDepositLimitsLoading || isUpdating,
      'data-track-container': getTrackTypeForPeriod(period),
      'data-testid': `deposit-limit-${period}`,
      'button-data-testid': `deposit-limit-${period}-button`,
      'data-testid-value': 'deposit-limit-value',
      'data-testid-status': 'deposit-limit-status',
    };
  };

  const depositLimitsItems: EditableNavigationMenuItemProps[] = (['DAY', 'WEEK', 'MONTH'] as const).map(createDepositLimitItem);

  const timeLimitsItems: EditableNavigationMenuItemProps[] = [
    ...(saferGambling.showRealityCheck
      ? [
          {
            label: t('time_limits.reality_check'),
            data: realityCheckData?.value ? t(`reality_check.intervals.${realityCheckData.value}`) : t('reality_check.intervals.NO_SET'),
            variant: 'editable' as const,
            buttonText: realityCheckData?.value ? t('edit') : t('time_limits.request'),
            chevron: false,
            onClick: () => {
              setShowRealityCheckSheet(true);
            },
            isLoading: isRealityCheckLoading,
            'data-track-container': 'Reality Check Reminder',
            'data-track-name': 'Reality Check Reminder',
            'data-testid': 'reality-check-section',
            'button-data-testid': 'reality-check-section-button',
            'data-testid-value': 'reality-check-value',
          },
        ]
      : []),
    {
      label: t('time_limits.time_out'),
      data: saferGambling.showBreakToolsNotSetStatus ? t('time_limits.not_set') : <></>,
      variant: 'editable',
      buttonText: t('time_limits.request'),
      chevron: false,
      onClick: () => {
        setShowTimeOutSheet(true);
      },
      description: t('time_limits.time_out_description'),
      'data-track-container': 'Timeout',
      'data-testid': 'time-out-section',
      'button-data-testid': 'time-out-section-button',
      ...(saferGambling.showBreakToolsNotSetStatus ? { 'data-testid-value': 'time-out-value' } : {}),
    },
    {
      label: t('time_limits.self_exclusion.title'),
      data: saferGambling.showBreakToolsNotSetStatus ? t('time_limits.not_set') : <></>,
      variant: 'editable',
      buttonText: t('time_limits.request'),
      chevron: false,
      onClick: () => {
        setShowSelfExclusionSheet(true);
      },
      description: t('time_limits.self_exclusion.description'),
      'data-track-container': 'Self-exclusion',
      'data-testid': 'self-exclusion-section',
      'button-data-testid': 'self-exclusion-section-button',
      ...(saferGambling.showBreakToolsNotSetStatus ? { 'data-testid-value': 'self-exclusion-value' } : {}),
    },
  ];

  const otherResourcesItems: LinkNavigationMenuItemProps[] = saferGambling.otherResources.map(resource => ({
    data: t(`other_resources.${resource.translationKey}`),
    chevron: false,
    expand: true,
    variant: 'link',
    href: resource.href,
    target: '_blank',
    rel: 'noopener',
    'data-track-container': resource.trackName,
    'data-track-name': resource.trackName,
  }));

  return (
    <div ref={settingsContainerRef} className="flex flex-col gap-6" data-track-container="Settings" data-testid="safer-gambling-container">
      {/* MVP: scheduled safer gambling call — restore when we support it
      {user.notification ? (
        <StatusNotification
          type={user.notification.type}
          label={user.notification.label}
          description={user.notification.description}
          showChevron={true}
          onClick={() => {
            router.push(user.notification!.link);
          }}
          dataTrackMessage={capitalizeWords(user.notification.label)}
        />
      ) : null}
      */}

      <p>
        {t('header_text')}{' '}
        <Link href={saferGambling.learnMoreHref} target="_blank" rel="noopener" data-track-name="Set Limits Info">
          {t('learn_more')}
        </Link>
      </p>

      <div className="flex flex-col gap-4" data-track-section="Deposit Limits">
        <div className="flex flex-col gap-0.5">
          <p className="paragraph-strong">{t('deposit_limits.title')}</p>
          <p className="paragraph-s text-foreground-subtle">{t('deposit_limits.description')}</p>
        </div>
        <NavigationMenu variant="secondary" items={depositLimitsItems} className={className} data-testid="deposit-limits-menu" />
      </div>

      <div className="flex flex-col gap-4" data-track-section="Time Management">
        <p className="paragraph-strong">{t('time_limits.title')}</p>
        <NavigationMenu variant="secondary" items={timeLimitsItems} className={className} data-testid="time-limits-menu" />
      </div>

      <div className="flex flex-col gap-4" data-track-section="Other Resources">
        <div className="flex flex-col gap-0.5">
          <p className="paragraph-strong">{t('other_resources.title')}</p>
          <p className={cn(isAustralianMarket ? 'paragraph-explanatory' : 'paragraph-s', 'text-foreground-subtle')}>{t('other_resources.description')}</p>
        </div>
        <NavigationMenu variant="secondary" items={otherResourcesItems} className={className} data-testid="other-resources-menu" />
      </div>

      <SetDepositLimitSheet
        open={showSetDepositLimitSheet}
        onOpenChange={setShowSetDepositLimitSheet}
        user={{ id: session?.profile?.id || '', firstName: session?.profile?.firstName || '' }}
        limitPeriod={limitPeriod}
        limits={depositLimits?.limits || []}
        onSaving={isSaving => {
          setUpdatingPeriod(isSaving ? limitPeriod : null);
        }}
        trigger={<HiddenTrigger label={t(`deposit_limits.${getTranslationKeyForPeriod(limitPeriod)}`)} />}
      />

      {saferGambling.showRealityCheck && (
        <RealityCheckSheet
          open={showRealityCheckSheet}
          onOpenChange={setShowRealityCheckSheet}
          currentInterval={realityCheckData?.value}
          trigger={<HiddenTrigger label={realityCheckData?.value ? t('edit') : t('time_limits.request')} />}
        />
      )}

      <SelfExclusionSheet open={showSelfExclusionSheet} onOpenChange={setShowSelfExclusionSheet} trigger={<HiddenTrigger label={t('time_limits.self_exclusion.title')} />} />

      <TimeOutSheet open={showTimeOutSheet} onOpenChange={setShowTimeOutSheet} trigger={<HiddenTrigger label={t('time_limits.request')} />} />
    </div>
  );
}

export { getDaysUntilPendingLimitValidFrom, getHoursUntilPendingLimitValidFrom, SaferGambling };
